// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.

import type { ReactNode } from 'react'

export interface NextStepAction {
  icon:     ReactNode
  label:    string
  onClick:  () => void
  primary?: boolean
}

/**
 * Procesbegeleider-blok aan het einde van een module.
 * Helpt de gebruiker bij de logische vervolgstap (voorbereiding op een gesprek
 * met de juiste professional). Toekomstbestendig: het `actions`-veld is een
 * vrije lijst, zodat later eenvoudig acties kunnen worden toegevoegd
 * (aangesloten professionals, beveiligd verzenden, ontvangstbevestiging,
 * verzendstatus, afspraak plannen) zonder de opmaak te wijzigen.
 */
export function NextStepBlock({ title, intro, actions, disclaimer }: {
  title:       string
  intro:       ReactNode
  actions:     NextStepAction[]
  disclaimer?: string
}) {
  return (
    <div className="rounded-2xl border-2 border-sage-200 bg-sage-50/60 px-5 py-5 flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <p className="text-base font-semibold text-ink">{title}</p>
        <div className="text-sm text-ink-3 leading-relaxed">{intro}</div>
      </div>

      <div className="flex flex-col gap-2">
        {actions.map((a, i) => (
          <button
            key={i}
            type="button"
            onClick={a.onClick}
            className={`flex items-center gap-2.5 w-full rounded-xl px-4 py-3 text-sm font-medium transition-all active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sage-600 ${
              a.primary
                ? 'bg-moss text-white hover:bg-moss-light shadow-soft'
                : 'bg-white border border-sand-300 text-ink hover:border-sage-300 hover:bg-sage-50'
            }`}
          >
            <span className="flex-shrink-0 text-base leading-none" aria-hidden="true">{a.icon}</span>
            <span className="text-left min-w-0">{a.label}</span>
          </button>
        ))}
      </div>

      {disclaimer && (
        <p className="text-xs text-ink-4 leading-relaxed border-t border-sage-200 pt-3">{disclaimer}</p>
      )}
    </div>
  )
}
